import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ModalService } from '@app/services/modal/modal.service';
import { MsalAuthService } from '@app/services/msal-auth/msal-auth.service';
import { AppStore } from '@app/stores/app/app.store';
import { IModalFormData } from '@app/types/modal-form-data';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { CountdownComponent } from '../countdown/countdown.component';
import { FormModeType } from '@app/enums/form-mode-type.enum';
import { FormGroup } from '@angular/forms';
import { CountDownPipe } from '@app/pipes/countdown/countdown-pipe';
import { formatString } from '@app/helpers/text/format-string';
import { environment } from '@env/environment';
import { IdleStateType } from '@app/enums/idle-state-type.enum';

@Component({
    selector: 'app-session-time-out',
    templateUrl: './session-time-out.component.html',
    styleUrls: ['./session-time-out.component.scss'],
    imports: [NgIdleKeepaliveModule],
    providers: [Idle, Keepalive, CountDownPipe],
})
export class SessionTimeOutComponent implements OnInit, OnDestroy {
    private idle = inject(Idle);
    private countdownPipe = inject(CountDownPipe);
    private keepAlive = inject(Keepalive);
    private msalAuthService = inject(MsalAuthService);
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    private modalService = inject(ModalService);

    state = signal({
        idleState: IdleStateType.Started as IdleStateType,
        isTimeOut: false,
        lastPing: null as Date | null,
        countdown: 300,
        showModal: false,
    });
    idleState = computed(() => this.state().idleState);
    isTimeOut = computed(() => this.state().isTimeOut);
    lastPing = computed(() => this.state().lastPing);
    countdown = computed(() => this.state().countdown);
    showModal = computed(() => this.state().showModal);
    modalFormData: IModalFormData | undefined;

    ngOnInit(): void {
        this.idle.setIdle(environment.idleTimeOut);
        this.idle.setTimeout(environment.countDownWarningTimeOut);
        // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.handleIdleEnd();
        this.handleTimeout();
        this.handleIdleStart();
        this.handleTimeoutWarning();

        // sets the ping interval to 15 seconds
        this.keepAlive.interval(15);
        void this.keepAlive.onPing.subscribe(() => {
            this.state.update((state) => ({
                ...state,
                lastPing: new Date(),
            }));
        });

        this.initIdle();
    }

    handleIdleEnd() {
        void this.idle.onIdleEnd.subscribe(() => {
            this.state.update((state) => ({
                ...state,
                idleState: IdleStateType.Ended,
            }));
        });
    }

    handleTimeout() {
        void this.idle.onTimeout.subscribe(() => {
            this.state.update((state) => ({
                ...state,
                idleState: IdleStateType.TimedOut,
                isTimeOut: true,
                lastPing: null,
            }));
            this.msalAuthService.logout(); //remove this line once
        });
    }

    handleIdleStart() {
        void this.idle.onIdleStart.subscribe(() => {
            this.state.update((state) => ({
                ...state,
                idleState: IdleStateType.Idle,
            }));
            if (!this.showModal()) this.showIdleTimeOutWarningModal();
        });
    }
    handleTimeoutWarning() {
        void this.idle.onTimeoutWarning.subscribe((countdown) => {
            this.state.update((state) => ({
                ...state,
                idleState: IdleStateType.Warning,
                countdown: countdown,
                showModal: true,
            }));
        });
    }

    initIdle() {
        this.idle.watch();
        this.state.update((state) => ({
            ...state,
            idleState: IdleStateType.Started,
            showModal: false,
            isTimeOut: false,
        }));
    }

    showIdleTimeOutWarningModal() {
        return; //do nothing for now , waiting for ui/ux approval
        const { MESSAGE__InactivityWarning, SessionTimeOutWarning } = this.translate();
        const countdownWarningTimeOut = this.countdownPipe.transform(
            environment.countDownWarningTimeOut,
            'm',
        );
        const modalData = {
            title: SessionTimeOutWarning,
            mode: FormModeType.ADD,
            fg: new FormGroup({}),
            data: formatString(MESSAGE__InactivityWarning, countdownWarningTimeOut),
        };
        this.state.update((state) => ({
            ...state,

            showModal: false,
        }));
        void this.modalService
            .showForm(CountdownComponent, modalData, {
                width: '500px',
                autoFocus: false,
                disableClose: true,
            })
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.idleState();
                    this.state.update((state) => ({
                        ...state,
                        showModal: false,
                        isTimeOut: false,
                    }));
                    void this.msalAuthService.getAccessToken();
                } else {
                    this.msalAuthService.logout();
                }
            });
    }

    ngOnDestroy(): void {
        this.idle.stop();
        this.keepAlive.stop();
    }
}
